/**
 * OAuthConnectorCallbackPage — landing page for Google's redirect.
 *
 * Mounted at `/oauth/connector-callback/:slug?code=…&state=…`. Reads
 * the slug + URL params, posts them to
 * `POST /connectors/{slug}/oauth/complete`, persists the resulting
 * `Connected` handle, then routes the user back to /integrations.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { Button } from '@/components/Button';
import { Spinner } from '@/components/LoadingState';
import {
  completeOAuth,
  type ConnectorSlug,
} from '@/api/external-connectors';
import {
  consumeOAuthPending,
  saveHandle,
} from '@/features/integrations/handle-storage';

const VALID_SLUGS: ReadonlySet<ConnectorSlug> = new Set([
  'google_drive',
  'gmail',
  'google_calendar',
] as ConnectorSlug[]);

export function OAuthConnectorCallbackPage() {
  const params = useParams<{ slug: string }>();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const ranRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const slug = params.slug as ConnectorSlug | undefined;
    const code = search.get('code');
    const state = search.get('state');
    const errParam = search.get('error');

    if (errParam) {
      setError(`Provider returned an error: ${errParam}`);
      return;
    }
    if (!slug || !VALID_SLUGS.has(slug)) {
      setError(`Unknown connector slug: ${slug ?? '(missing)'}`);
      return;
    }
    if (!code || !state) {
      setError('Missing code or state in the callback URL.');
      return;
    }

    // Verify the state matches the one we recorded before redirecting.
    const pending = consumeOAuthPending();
    if (pending && pending.state !== state) {
      setError('State mismatch — this callback was not issued for this browser.');
      return;
    }

    completeOAuth(slug, { code, state })
      .then((res) => {
        if (res.handle.kind !== 'connected') {
          setError('Server did not return a connected handle.');
          return;
        }
        saveHandle(slug, res.handle);
        setDone(true);
        // Brief delay so the success state is visible before redirect.
        window.setTimeout(() => navigate('/integrations', { replace: true }), 600);
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : String(e));
      });
  }, [navigate, params.slug, search]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-base px-6">
      <div className="w-full max-w-md rounded-lg border border-border bg-surface p-8 text-center shadow-subtle">
        {error ? (
          <>
            <h1 className="text-lg font-semibold text-danger">Could not finish connecting</h1>
            <p className="mt-2 text-sm text-ink-muted">{error}</p>
            <div className="mt-6">
              <Button variant="secondary" size="sm">
                <Link to="/integrations">Back to Integrations</Link>
              </Button>
            </div>
          </>
        ) : done ? (
          <>
            <h1 className="text-lg font-semibold text-ink">Connected</h1>
            <p className="mt-2 text-sm text-ink-muted">Returning to Integrations…</p>
          </>
        ) : (
          <>
            <div className="flex justify-center">
              <Spinner size="md" />
            </div>
            <p className="mt-4 text-sm text-ink-muted">Finishing the OAuth handshake…</p>
          </>
        )}
      </div>
    </div>
  );
}
